import { useState } from 'react';

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'How much does it cost to have a vending machine installed?',
      answer: 'There is no cost for installation! We provide free vending machine placement, installation, and setup. Our business model is based on product sales, so there are no upfront fees, rental charges, or hidden costs to your business.'
    },
    {
      question: 'What types of products can you stock in the machines?',
      answer: 'We offer a wide variety of products including traditional snacks, healthy options, fresh food, beverages (both cold and hot), and specialty items. We work with you to customize the product selection based on your employees\' preferences and dietary needs.'
    },
    {
      question: 'How often are the machines restocked?',
      answer: 'Restocking frequency depends on your location\'s usage, but typically we restock weekly or bi-weekly. We monitor inventory levels and adjust our schedule to ensure your machine never runs empty. High-traffic locations may receive more frequent service.'
    },
    {
      question: 'What happens if the machine breaks down?',
      answer: 'We provide 24/7 emergency support for all our machines. If you experience any issues, simply call our support line and we\'ll dispatch a technician immediately. Most issues are resolved within 4 hours, and we always prioritize keeping your machine operational.'
    },
    {
      question: 'Do the machines accept credit cards and mobile payments?',
      answer: 'Yes! All our modern vending machines are equipped with cashless payment systems that accept credit cards, debit cards, Apple Pay, Google Pay, and other mobile payment options, in addition to traditional cash payments.'
    },
    {
      question: 'What is the minimum space required for a vending machine?',
      answer: 'Standard vending machines require approximately 3 feet wide by 3 feet deep of floor space, with at least 6 feet of ceiling clearance. We also offer compact models for smaller spaces. Our team will assess your location during the consultation to recommend the best fit.'
    },
    {
      question: 'Can we request specific brands or products?',
      answer: 'Absolutely! We work with all major brands and can accommodate special requests. During setup, we\'ll discuss your preferences and can even conduct employee surveys to determine the most popular items for your location.'
    },
    {
      question: 'Is there a contract or long-term commitment required?',
      answer: 'We offer flexible agreements with no long-term contracts required. While we hope to build lasting partnerships, you\'re free to discontinue service with 30 days\' notice. Our goal is to earn your business through excellent service, not binding contracts.'
    },
    {
      question: 'How do you ensure product freshness?',
      answer: 'We implement strict quality control measures including regular inventory rotation, expiration date monitoring, and temperature-controlled storage for perishable items. Products are checked during every restocking visit, and any items approaching expiration are removed and replaced.'
    },
    {
      question: 'Can you provide sales reports and analytics?',
      answer: 'Yes! We provide detailed monthly reports showing sales data, popular products, peak usage times, and inventory trends. This information helps us optimize your product selection and can provide valuable insights into your workplace culture and preferences.'
    }
  ];

  return (
    <section id="faq" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-sm font-semibold text-orange-600 tracking-widest uppercase mb-4 block">
            FAQ
          </span>
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Frequently Asked<br />Questions
          </h2>
          <p className="text-lg text-gray-600">
            Everything you need to know about our vending machine services
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-8 py-6 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <h4 className="text-lg font-bold text-gray-900 pr-8">{faq.question}</h4>
                <div className={`w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-teal-600 text-xl"></i>
                </div>
              </button>
              
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-8 pb-6">
                  <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-12 text-white">
          <h3 className="text-3xl font-bold mb-4">Still Have Questions?</h3>
          <p className="text-lg text-teal-100 mb-8 max-w-2xl mx-auto">
            Our team is here to help! Contact us for personalized answers and a free consultation.
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-white text-teal-600 px-8 py-4 rounded-full text-base font-semibold hover:bg-gray-100 transition-colors shadow-lg cursor-pointer whitespace-nowrap"
          >
            Contact Us Now
          </button>
        </div>
      </div>
    </section>
  );
}
